import { X, ArrowRight } from "lucide-react"
import { useNavigate } from "react-router"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { studentReports } from "../../../../lib/report-data"

const StudentDetailsModal = ({ isOpen, onClose, student }) => {
  const navigate = useNavigate()

  if (!isOpen || !student) return null

  const report = studentReports[student.id]

  const handleViewOverview = () => {
    navigate(`/student-overview?id=${student.id}`)
  }

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-bold text-white">Student Report Details</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-140px)]">
            {/* Student Info */}
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div
                  className={`h-16 w-16 rounded-full flex items-center justify-center border-2 border-white shadow-md ${student.initialsColor === "orange"
                      ? "bg-orange-100 text-orange-600"
                      : student.initialsColor === "purple"
                        ? "bg-purple-100 text-purple-600"
                        : student.initialsColor === "pink"
                          ? "bg-pink-100 text-pink-600"
                          : student.initialsColor === "indigo"
                            ? "bg-indigo-100 text-indigo-600"
                            : student.initialsColor === "teal"
                              ? "bg-teal-100 text-teal-600"
                              : student.initialsColor === "blue"
                                ? "bg-blue-100 text-blue-600"
                                : student.initialsColor === "green"
                                  ? "bg-green-100 text-green-600"
                                  : student.initialsColor === "amber"
                                    ? "bg-amber-100 text-amber-600"
                                    : student.initialsColor === "red"
                                      ? "bg-red-100 text-red-600"
                                      : "bg-cyan-100 text-cyan-600"
                    }`}
                >
                  <span className="font-bold text-xl">{student.initials}</span>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">{student.name}</h4>
                  <p className="text-sm text-gray-600">
                    {student.class} • ID: {student.studentId}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">GPA: {student.gpa} / 5.0</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Guardian Name</p>
                  <p className="text-sm font-semibold text-gray-900">{student.guardianName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Guardian Contact</p>
                  <p className="text-sm font-semibold text-gray-900">{student.guardianPhone}</p>
                  {student.guardianEmail && (
                    <p className="text-xs text-indigo-600">{student.guardianEmail}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Subjects */}
            <div className="mb-6">
              <h5 className="text-md font-bold text-gray-900 mb-3">Current Subjects</h5>
              <div className="flex flex-wrap gap-2">
                {report?.subjects?.map((subject) => (
                  <Badge key={subject} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {subject}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Career Paths */}
            <div className="mb-6">
              <h5 className="text-md font-bold text-gray-900 mb-3">Career Path Interest</h5>
              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                {student.careerPath}
              </Badge>
            </div>

            {/* Results */}
            <div className="mb-6">
              <h5 className="text-md font-bold text-gray-900 mb-3">Academic Results (First Term)</h5>
              <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="p-3 text-left font-semibold text-gray-500">Subject</th>
                      <th className="p-3 text-center font-semibold text-gray-500">Score</th>
                      <th className="p-3 text-center font-semibold text-gray-500">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report?.results?.map((result) => (
                      <tr key={result.subject} className="border-b border-gray-200 last:border-0">
                        <td className="p-3 text-gray-900">{result.subject}</td>
                        <td className="p-3 text-center font-semibold text-gray-900">{result.score}</td>
                        <td className="p-3 text-center">
                          <Badge
                            variant="outline"
                            className={
                              result.score >= 90
                                ? "bg-green-100 text-green-800 border-green-200"
                                : result.score >= 80
                                  ? "bg-blue-100 text-blue-800 border-blue-200"
                                  : result.score >= 70
                                    ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                    : "bg-orange-100 text-orange-800 border-orange-200"
                            }
                          >
                            {result.grade}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Teacher's Remarks */}
            <div className="mb-6">
              <h5 className="text-md font-bold text-gray-900 mb-3">Teacher's Remarks</h5>
              <div className="bg-blue-50 border-l-4 border-indigo-500 p-4 rounded-r">
                <p className="text-sm text-gray-800 italic">"{report?.teacherRemarks}"</p>
              </div>
            </div>

            {/* Extracurricular Activities */}
            <div className="mb-6">
              <h5 className="text-md font-bold text-gray-900 mb-3">Extracurricular Activities</h5>
              <ul className="list-disc list-inside space-y-1">
                {report?.extracurriculars?.map((activity) => (
                  <li key={activity} className="text-sm text-gray-800">
                    {activity}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100 text-gray-700"
            >
              Close
            </Button>
            <Button
              onClick={handleViewOverview}
              className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              View Full Overview
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentDetailsModal